"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import type { Profile, Situation } from "@/generated/fastapi";
import { useSituationsQuery, useSituationOptions } from "@/features/situations";
import { useUserProfile } from "@/state/globalStore";
import { useAuthData } from "./AuthDataProvider";

interface CoreDataContextType {
  isCoreDataReady: boolean;
  fetchSituations: (forceRefresh?: boolean) => Promise<void>;
  refetchSituations: () => Promise<void>;
  situationsOptions: ReturnType<typeof useSituationOptions>;
}

const CoreDataContext = createContext<CoreDataContextType | undefined>(
  undefined,
);

export function CoreDataProvider({
  children,
  initSituations,
  initProfile,
}: {
  children: React.ReactNode;
  initSituations: Situation[];
  initProfile: Profile;
}) {
  const { setUserProfile, initFetchedUserProfile, setInitFetchedUserProfile } =
    useUserProfile();
  const { isAuthReady } = useAuthData();

  const situationsQuery = useSituationsQuery({
    initialData: initSituations,
  });

  useEffect(() => {
    if (initProfile && !initFetchedUserProfile) {
      setUserProfile(initProfile);
      setInitFetchedUserProfile(true);
    }
  }, [
    initFetchedUserProfile,
    initProfile,
    setInitFetchedUserProfile,
    setUserProfile,
  ]);

  const fetchSituations = useCallback(
    async (forceRefresh = false) => {
      if (situationsQuery.isFetching) return;

      if (!forceRefresh && situationsQuery.data) {
        return;
      }

      await situationsQuery.refetch();
    },
    [situationsQuery],
  );

  const refetchSituations = useCallback(async () => {
    await situationsQuery.refetch();
  }, [situationsQuery]);

  const situationsOptions = useSituationOptions(situationsQuery.data);

  const isCoreDataReady = useMemo(() => {
    return (
      isAuthReady &&
      typeof situationsQuery.data !== "undefined" &&
      !situationsQuery.isLoading &&
      !situationsQuery.isFetching
    );
  }, [
    isAuthReady,
    situationsQuery.data,
    situationsQuery.isFetching,
    situationsQuery.isLoading,
  ]);

  return (
    <CoreDataContext.Provider
      value={{
        isCoreDataReady,
        fetchSituations,
        refetchSituations,
        situationsOptions,
      }}
    >
      {children}
    </CoreDataContext.Provider>
  );
}

export function useCoreData() {
  const context = useContext(CoreDataContext);
  if (context === undefined) {
    throw new Error("useCoreData must be used within a CoreDataProvider");
  }
  return context;
}
